import React, {useEffect, useState} from "react";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {IPartials, useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import {LoginMode} from "../../Checkout";
import Spinner from "../../../utils/loader/Spinner";
import {convertToAddressData} from "../AddressStep/AddressStep";
import {AddressResponseData} from "@connox/typescript-library";
import {emailRegExp} from "../../../lib/ModalWindow/ForgotPasswordModal";
import styles from "./NewCustomer.module.scss";
import {useCustomerContext} from "../../../customer/CustomerContextProvider/CustomerContextProvider";
import {getShopId} from "../../../lib/getShopId";

type IRegisterFormData = Pick<AddressResponseData, "email" | "firstName" | "lastName" | "gender"> & {
  isGuest: boolean;
  isCorporateCustomer: boolean;
  countryCode: string;
};

const NewCustomer: React.FC = () => {
  const {customer} = useCustomerContext();
  const {checkout} = useCheckoutContext();
  const {setViewState} = useCartContext();
  const {t} = useTranslation("checkout");
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    watch,
    reset,
    handleSubmit,
    formState: {errors},
  } = useForm<IRegisterFormData>({
    mode: "onSubmit",
    defaultValues: {
      ...checkout.invoiceAddress,
      isGuest: checkout.isGuest,
      isCorporateCustomer: checkout.cartData.isCorporateCustomer,
      email: customer.email || checkout.invoiceAddress?.email,
      countryCode: checkout.cartData.countryCode,
    },
  });

  useEffect(() => {
    return () => {
      if (isLoading) {
        setIsLoading(false);
      }
    };
  }, [isLoading]);

  useEffect(() => {
    if (!checkout.invoiceAddress && customer.customerId) {
      void customer.retrieveProperties(getShopId()).then((props) => {
        reset({
          firstName: props?.firstName || undefined,
          lastName: props?.lastName || undefined,
          email: props?.email || undefined,
          gender: props?.gender || undefined,
        });
      });
    }
  }, []);

  const submitForm = async () => {
    const invoiceAddress = convertToAddressData(watch());
    const deliveryAddress = checkout.deliveryAddress || invoiceAddress;
    const partials: IPartials = {
      partialAddress: {
        email: invoiceAddress.email,
        firstName: invoiceAddress.firstName,
        lastName: invoiceAddress.lastName,
        gender: invoiceAddress.gender,
      },
      isCorporateCustomer: watch().isCorporateCustomer,
      isGuest: watch().isGuest,
    };
    setViewState({isRegistering: true, partials});
    setIsLoading(true);
    await checkout.put({
      address: {
        delivery: deliveryAddress,
        invoice: invoiceAddress,
      },
      cart: {
        isCorporateCustomer: watch().isCorporateCustomer,
      },
      isGuest: watch().isGuest,
    });
  };

  return (
    <React.Fragment>
      <h1>{t("Neu bei Connox?")}</h1>
      <form className={styles.registerForm}>
        <div className={styles.inputWrapper}>
          <input
            type={"text"}
            placeholder={t("Ihr Vorname *")}
            {...register("firstName", {required: true, maxLength: 50})}
          />
          {errors.firstName?.type === "required" && <span>{t("Bitte geben Sie einen Vornamen an")}</span>}
        </div>

        <div className={styles.inputWrapper}>
          <input
            type={"text"}
            placeholder={t("Ihr Nachname *")}
            {...register("lastName", {required: true, maxLength: 50})}
          />
          {errors.lastName?.type === "required" && <span>{t("Bitte geben Sie einen Nachnamen an")}</span>}
        </div>

        <div className={styles.inputWrapper}>
          <div className={styles.radioGroup}>
            <label>
              <input type="radio" id="f" value="F" {...register("gender", {required: true})} />
              {t("Weiblich")}
            </label>
            <label>
              <input type="radio" id="m" value="M" {...register("gender", {required: true})} />
              {t("Männlich")}
            </label>
          </div>
          {errors.gender?.type === "required" && <span>{t("Bitte geben Sie Ihr Geschlecht an")}</span>}
        </div>

        <div className={styles.inputWrapper}>
          <input
            type={"email"}
            placeholder={t("Ihre E-Mail-Adresse *")}
            {...register("email", {
              required: true,
              pattern: emailRegExp,
            })}
          />
          {errors.email?.type === "required" && <span>{t("Bitte ausfüllen")}</span>}
          {errors.email?.type === "pattern" && watch().email && (
            <span>{t("Bitte geben Sie eine gültige E-Mail-Adresse an")}</span>
          )}
        </div>

        <div className={styles.inputWrapper}>
          <label>
            <input type={"checkbox"} {...register("isCorporateCustomer")} />
            {t("Ich möchte als Geschäftskunde einkaufen")}
          </label>
          <label>
            <input type={"checkbox"} {...register("isGuest")} />
            {t("Ich möchte als Gast einkaufen")}
          </label>
        </div>

        <p>{t("Mit der Registrierung erklären Sie sich mit unseren Datenschutzbestimmungen einverstanden.")}</p>

        <Button
          btnIdentifier={LoginMode.NEWCUSTOMER}
          type={isLoading ? ButtonType.disabled : ButtonType.default}
          action={(evt) => {
            evt.preventDefault();
            void handleSubmit(submitForm)();
          }}
          styles={[ButtonStyles.wide]}
        >
          {isLoading ? <Spinner /> : <span>{t("Weiter")}</span>}
        </Button>
      </form>
    </React.Fragment>
  );
};

export default NewCustomer;
